import { GlobalStore, GridStore } from '@roc/feature-app-core';
import { InternalValuationLoan, InternalValuationTab } from '../types/internalValuationTypes';
import { action, flow, makeObservable, observable } from 'mobx';
import { InternalValuationService } from '../services/internalValuationService';
import { LoanService, LoanStore } from '@roc/feature-loans';


export class InternalValuationStore {
  globalStore: GlobalStore;
  tab: InternalValuationTab;
  public valuationReviewers: any[];
  selectedRecords: InternalValuationLoan[] = [];
  internalValuationLoansGridStore: GridStore;
  internalValuationService: InternalValuationService;
  loanStore: LoanStore;
  loanService: LoanService;
  paymentUrl: string;

  constructor(globalStore: GlobalStore, loanStore: LoanStore) {
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.setDefaults();
    this.internalValuationService = new InternalValuationService();
    this.loanService = new LoanService();

    this.internalValuationLoansGridStore = new GridStore(
      () => this.fetchInternalValuationTasks(),
      null,
      50
    );

    makeObservable(this, {
      tab: observable,
      setTab: action,
      reset: action,
      selectedRecords: observable,
      setSelectedRecords: action,
      removeRecord: action,
      clearSelectedRecords: action,
      valuationReviewers: observable,
      fetchValuationReviewers: flow,
      updateValuationReviewer: flow,
      bulkUpdateValuationReviewer: flow,
      paymentUrl: observable,
      fetchPaymentLink: flow,
      requestInternalValuationPayment: flow
    })
  }

  setSelectedRecords(record: InternalValuationLoan, selected: boolean): boolean {
    if (selected) {
      this.selectedRecords.push(record);
      return true;
    } else {
      this.removeRecord(record);
      return false;
    }
  }
  *fetchPaymentLink(loanId: any) {
    try {
      const response = yield this.internalValuationService.fetchPaymentLink(loanId);
      this.paymentUrl = response.data.data?.paymentLink ?? '';
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching RealVal payment link.',
      });
    }
  }

  removeRecord(record: InternalValuationLoan) {
    this.selectedRecords = this.selectedRecords.filter(r => r.loanId !== record.loanId);
  }

  clearSelectedRecords() {
    this.selectedRecords = [];
  }

  getInternalValuationLoanCountsBySubStatus(): { subStatus: string; count: number; }[] {
    const rows = this.internalValuationLoansGridStore.gridData.data.rows ?? [];

    const counts = rows.reduce(
      (acc, row) => {
        acc[row.subStatus] = (acc[row.subStatus] || 0) + 1;
        return acc;
      },
      {
        'DATA_ENTRY_NO_APPRAISAL': 0,
        'DATA_ENTRY_APPRAISAL': 0,
        'STRUCTURING': 0,
        'UNDERWRITING': 0,
        'PENDING_DECISION': 0,
        'TOTAL': 0
      } as Record<string, number>
    );

    counts['TOTAL'] = rows.length;

    const order = [
      'UNDERWRITING',
      'STRUCTURING',
      'PENDING_DECISION',
      'DATA_ENTRY_NO_APPRAISAL',
      'DATA_ENTRY_APPRAISAL',
      'TOTAL'
    ];

    return order.map(subStatus => ({
      subStatus,
      count: counts[subStatus] ?? 0,
    }));
  }

  reset() {
    this.setDefaults();
    this.internalValuationLoansGridStore.reset();
    this.clearSelectedRecords();
  }

  setDefaults() {
    this.tab = InternalValuationTab.ALL_LOANS;
    this.paymentUrl = "payment link";
  }

  setTab(value: InternalValuationTab) {
    this.tab = value;
  }

  *requestInternalValuationPayment(entityId: any) {
    try {
      yield this.internalValuationService.sendEmailToBorrowerPaymentLink(entityId, 'Internal_Valuation_Loan');
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payment email successfully sent',
      });
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error on sending payment email',
      });
    }
  }

  *fetchValuationReviewers() {
    try {
      yield this.loanStore.fetchSpecialistsDropdown();
      const { valuationReviewers } = this.loanStore.specialistsDropdown;
      this.valuationReviewers = valuationReviewers;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching valuation reviewers.',
      });
    }
  }

  *updateValuationReviewer(loanId: number, valuationReviewerId: number) {
    try {
      yield this.internalValuationService.updateValuationReviewer(loanId, {
        valuationReviewerId: valuationReviewerId,
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating the valuation reviewer.',
      });
    }
  }

  *bulkUpdateValuationReviewer(valuationReviewerId: number) {
    try {
      const loanIds = this.selectedRecords.map((record: InternalValuationLoan) => record.loanId);
      yield this.internalValuationService.bulkUpdateLoansValuationReviewer(loanIds, valuationReviewerId);
      this.internalValuationLoansGridStore.resetAndFetchGridData();
      this.clearSelectedRecords();
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating the valuation reviewer.',
      })
    }
  }

  private async fetchInternalValuationLoans() {
    try {
      this.clearSelectedRecords();
      const gridFilters = this.internalValuationLoansGridStore.gridData.meta.filters ?? {};
      const filters = {
        ...gridFilters,
        ...this.internalValuationLoansGridStore.gridData.meta
          .dropdownFilters?.priority,
      };
      const { pageNumber, pageSize } = this.internalValuationLoansGridStore.gridData.meta;
      const start = (pageNumber - 1) * pageSize + 1;
      const dropdownFilters =
        this.internalValuationLoansGridStore.gridData.meta.dropdownFilters || {};

      if (this.tab === InternalValuationTab.ALL_LOANS) {
        return await this.internalValuationService.getInternalValuationDashboard(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      } else {
        return await this.internalValuationService.getInternalValuationProgramDashboard(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching internal valuation dashboard loans.',
      });
    }
  }

  private async fetchInternalValuationTasks() {
    try {
      this.clearSelectedRecords();
      const gridFilters = this.internalValuationLoansGridStore.gridData.meta.filters ?? {};
      const filters = {
        ...gridFilters,
        ...this.internalValuationLoansGridStore.gridData.meta
          .dropdownFilters?.priority,
      };
      const { pageNumber, pageSize } = this.internalValuationLoansGridStore.gridData.meta;
      const start = (pageNumber - 1) * pageSize + 1;
      const dropdownFilters =
        this.internalValuationLoansGridStore.gridData.meta.dropdownFilters || {};

      if (this.tab === InternalValuationTab.ALL_LOANS) {
        return await this.internalValuationService.getInternalValuationTasksDashboard(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      } else if (this.tab === InternalValuationTab.DISPUTE_SECOND_APPRAISAL) {
        return await this.internalValuationService.getDisputeSecondAppraisalTasks(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      } else if (this.tab === InternalValuationTab.RENTAL_RISK_PROFILER_REVIEW) {
        return await this.internalValuationService.getRentalRiskTasksDashboard(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      } else if (this.tab === InternalValuationTab.DISPUTE_APPRAISAL) {
        return await this.internalValuationService.getDisputeAppraisalDashboard(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      } else {
        return await this.internalValuationService.getInternalValuationProgramTasksDashboard(
          start,
          this.internalValuationLoansGridStore.gridData.meta.pageSize,
          this.internalValuationLoansGridStore.gridData.meta.sortDir,
          this.internalValuationLoansGridStore.gridData.meta.sortBy,
          filters,
          dropdownFilters
        );
      }
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching internal valuation dashboard tasks.',
      });
    }
  }
}
